import React, { lazy, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, useParams } from "react-router-dom";
import Layout from "./components/layout/Layout";
import { LEGACY_SERVICE_SLUGS } from "./data/services";

// Redirect legacy hash-based URLs (e.g. /#/about → /about)
// This ensures any old links or bookmarks using HashRouter format still work.
function HashRedirect() {
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        // If the page loads at "/" but has a hash that looks like a route (e.g. /#/about)
        const hash = window.location.hash;
        if (hash && hash.startsWith("#/")) {
            const cleanPath = hash.replace("#", "");
            navigate(cleanPath, { replace: true });
        }
    }, [navigate, location]);

    return null;
}

/**
 * Redirects renamed service URLs to their new slug so existing search
 * rankings and bookmarks keep working. Slugs mapped to null (IT Consulting)
 * fall back to the Services page, where the service is now described.
 */
function LegacyServiceRedirect() {
    const { slug } = useParams();
    const target = LEGACY_SERVICE_SLUGS[slug];
    return <Navigate to={target ? `/services/${target}` : "/services"} replace />;
}

const HomePage = lazy(() => import("./pages/HomePage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const ServiceDetailPage = lazy(() => import("./pages/ServiceDetailPage"));
const DecodePage = lazy(() => import("./pages/DecodePage"));
const WorkPage = lazy(() => import("./pages/WorkPage"));

const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));

export default function App() {
    return (
        <Router>
            <HashRedirect />
            <Layout>
                <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/about" element={<AboutPage />} />

                    {/* Services */}
                    <Route path="/services" element={<ServicesPage />} />
                    <Route path="/services/:slug" element={<ServiceDetailPage />} />

                    {/* Work */}
                    <Route path="/work" element={<WorkPage />} />

                    {/* D CODE ACADEMY */}
                    <Route path="/d-code-academy" element={<DecodePage />} />


                    {/* Blog */}
                    <Route path="/blog" element={<BlogPage />} />
                    <Route path="/blog/:slug" element={<BlogPostPage />} />

                    {/* Legal */}
                    <Route path="/privacy-policy" element={<PrivacyPage />} />
                    <Route path="/terms-and-conditions" element={<TermsPage />} />

                    {/* ============================================ */}
                    {/* LEGACY REDIRECTS — keep old URLs working     */}
                    {/* ============================================ */}
                    {/* The enquiry form now lives on the homepage. */}
                    <Route path="/contact" element={<Navigate to="/#enquiry" replace />} />
                    <Route path="/decode" element={<Navigate to="/d-code-academy" replace />} />
                    <Route path="/training" element={<Navigate to="/d-code-academy" replace />} />
                    <Route path="/careers" element={<Navigate to="/about" replace />} />
                    <Route path="/portfolio" element={<Navigate to="/work" replace />} />
                    <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
                    <Route path="/terms" element={<Navigate to="/terms-and-conditions" replace />} />

                    {/* Renamed service slugs */}
                    <Route path="/services/web-development" element={<LegacyServiceRedirect />} />
                    <Route path="/services/mobile-apps" element={<LegacyServiceRedirect />} />
                    <Route path="/services/custom-software" element={<LegacyServiceRedirect />} />
                    <Route path="/services/digital-marketing" element={<LegacyServiceRedirect />} />
                    <Route path="/services/it-consulting" element={<LegacyServiceRedirect />} />

                    <Route path="*" element={<NotFoundPage />} />
                </Routes>
            </Layout>
        </Router>
    );
}
